import React, { useEffect, useState } from "react";
import { Trans } from "../utils/i18n";
import {
    useFormState,
    useFormStateDispatch,
} from "../FormComponents/FormState";
import RadioButton from "./RadioBig";
import i18n from "i18next";
import { Field } from "react-final-form";
import RadioBigWithAmount from "./RadioBigWithAmount";
import { hasProducts } from "../config/orderEntryEnfore";
import { useGetProductCategories } from "../hooks/useOrderProductCategories";
import { ProductModal } from "./ProductModal";
import classNames from "classnames";
import { useUpdateFormProducts } from "../hooks/useUpdateFormProducts";
import { SelfRepairGuidesList } from "./SelfRepairGuidesList";
import { uniqBy } from "lodash";

const getModelFromDescription = (description) =>
    description.split("-")?.[0]?.trim();
const getColorFromDescription = (description) =>
    description.split("-")?.[1]?.trim();
const getVariantFromDescription = (description) =>
    description.split("-")?.[1]?.trim();

export function getColors(selectedCategory, model) {
    const colors = selectedCategory?.products
        .filter((item) => {
            const modelName = getModelFromDescription(item.description);
            return model === modelName;
        })
        .reduce((acc, item) => {
            const color = getColorFromDescription(item.description);

            if (acc.indexOf(color) === -1) {
                acc.push(color);
            }
            return acc;
        }, [])
        .filter(Boolean);

    return colors;
}

export function getProductConfigurationSteps(selectedCategory, model, disableColorSelection, enableVariantSelection) {
    const colors = getColors(selectedCategory, model);

    const PRODUCT_CONFIGURATION_STEPS = {
        CATEGORY_SELECTION: function () {
            return {
                enabled: !enableVariantSelection,
                isLastStep: false,
            };
        },
        PRODUCT_SELECTION: function () {
            return {
                enabled: !enableVariantSelection && !!selectedCategory,
                isLastStep: !this.COLOR_SELECTION().enabled,
            };
        },
        COLOR_SELECTION: function () {
            return {
                enabled:
                    !disableColorSelection &&
                    !!selectedCategory &&
                    model &&
                    colors &&
                    colors.length > 0,
                isLastStep: true,
            };
        },
        VARINAT_CATEGORY_SELECTION: function () {
            return {
                enabled: enableVariantSelection,
                isLastStep: false,
            };
        },
        VARINAT_SELECTION: function () {
            return {
                enabled: enableVariantSelection,
                isLastStep: true,
            };
        },
    };

    return PRODUCT_CONFIGURATION_STEPS;
}

export const ProductConfig = React.memo(
    function ({
        values,
        form,
        productValues,
        isOrderable = true,
        isForSales,
        maxQuantity,
        noProblems = true,
        disableColorSelection,
        enableVariantSelection,
        columns = 3,
        showSelfRepairGuides = false,
        showOtherAvailableDocuments=true,
        hideProducts = false,
        hideProductDetailsButton = false,
        hideUnavailableContactLink = false,
    }) {
        const orderEntryDispatch = useFormStateDispatch();
        const { categories, selectedCategory, selectedProduct } =
            useFormState();
        const model = values?.model;
        const color = values?.color;
        useGetProductCategories({
            // Only append these parameters if they are true, since the API
            // checks if they are present or not
            ...(noProblems ? { noProblems: true } : {}),
            ...(isForSales ? { isForSales: true } : {}),
            ...(isOrderable ? { isOrderable: true } : {}),
        }, {
            onSuccess: (response) => {
                if (!response.success) {
                    throw "error";
                }

                if (response.categories.length == 1) {
                    orderEntryDispatch({ type: 'setSelectedCategory', value: response.categories[0] });
                }

                orderEntryDispatch({
                    type: "setCategories",
                    value: response.categories,
                });
            }
        });
        const [isProductModalOpen, setIsProductModalOpen] = useState(false);
        const [variantCategory, setVariantCategory] = useState(null);
        const { addProduct, removeProduct, updateProduct, productExists, getProductFieldIndex } = useUpdateFormProducts({ form });
        
        const models = selectedCategory?.products.reduce((acc, item) => {
            const model = !disableColorSelection ? getModelFromDescription(item.description) : item.description;

            if (acc.indexOf(model) === -1) {
                acc.push(model);
            }
            return acc;
        }, []);
        const colors = getColors(selectedCategory, model);
        const PRODUCT_CONFIGURATION_STEPS = getProductConfigurationSteps(selectedCategory, model, disableColorSelection, enableVariantSelection);
        const setSelectedProduct = ({ isLastStep, product }) => {
            if (!isLastStep) {
                // Reset the product selection
                orderEntryDispatch({ type: "setSelectedProduct", value: null });
            } else {
                orderEntryDispatch({
                    type: "setSelectedProduct",
                    value: product,
                });
            }
        };
        const handleUpdateVariantCategory = (category) => {
            setVariantCategory(category);
            orderEntryDispatch({
                type: "setSelectedCategory",
                value: null,
            });
            form.change("model", null);
            form.change("color", null);
            setSelectedProduct({
                isLastStep: false,
            });
            orderEntryDispatch({
                type: "setSerialNumber",
                value: {},
            });
        };
        const handleUpdateSize = (category) => {
            orderEntryDispatch({
                type: "setSelectedCategory",
                value: category,
            });
            form.change("model", null);
            form.change("color", null);
            setSelectedProduct({
                isLastStep: false,
            });
            orderEntryDispatch({
                type: "setSerialNumber",
                value: {},
            });
        };
        const part_number = new URLSearchParams(location.search).get("part_number");
        const product_number = new URLSearchParams(location.search).get("product_number");
        const urlVariantCategory = product_number ? categories?.find(category => category.name === product_number) : null;
        const urlPart = urlVariantCategory ? urlVariantCategory.products.find(product => product.number == part_number) : null;

        useEffect( () => {
            if (!selectedCategory || !enableVariantSelection) {
                return;
            }
            const selectedCategoryModelName = getModelFromDescription(selectedCategory.description);
            const category = categories.find(category => category?.description?.includes(selectedCategoryModelName));

            setVariantCategory(category)
        }, [selectedCategory, categories, enableVariantSelection] );

        // Set variant category if we have a url variant category (model)
        useEffect(() => {
            if (!urlVariantCategory) {
                return;
            }

            handleUpdateVariantCategory(urlVariantCategory);
            handleUpdateSize(urlVariantCategory);
        }, [urlVariantCategory])

        // Add the part product to the form if we have a part_number in the url
        useEffect(() => {
            if (!urlPart) {
                return;
            }

            addProduct({
                ...urlPart,
                amount: 1,
                type: isForSales ? "shop" : null,
            });
        }, [urlPart, isForSales])

        if (!categories) {
            return (
                <div>
                    <Trans i18nKey="productSelect.loading">Loading</Trans>
                </div>
            );
        }

        return (
            <div>
                <div>
                    {PRODUCT_CONFIGURATION_STEPS.CATEGORY_SELECTION()
                        .enabled && (
                        <div className={"grid grid-cols-12 gap-6"}>
                            {categories.map((category) => (
                                <div
                                    className={classNames({
                                        "col-span-6":true, 
                                        "md:col-span-3":columns === 4, 
                                        "md:col-span-4":columns === 3, 
                                    })}
                                    key={category.id}
                                >
                                    <RadioButton
                                        input={{
                                            checked:
                                                selectedCategory?.id ===
                                                category.id,
                                            value: category.id,
                                            onChange: (e) => {
                                                orderEntryDispatch({
                                                    type: "setSelectedCategory",
                                                    value: category,
                                                });
                                                form.change("model", null);
                                                form.change("color", null);
                                                setSelectedProduct({
                                                    isLastStep: false,
                                                });
                                                orderEntryDispatch({
                                                    type: "setSerialNumber",
                                                    value: {},
                                                });
                                            },
                                            name: "category",
                                        }}
                                        field={{
                                            titel: category.description,
                                            image: category.image,
                                        }}
                                        meta={{}}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {PRODUCT_CONFIGURATION_STEPS.VARINAT_CATEGORY_SELECTION()
                        .enabled && (
                        <div className={"grid grid-cols-12 gap-6"}>
                            {uniqBy(categories, category => getModelFromDescription(category.description)).map((category) => (
                                <div
                                    className={classNames({
                                        "col-span-6":true, 
                                        "md:col-span-3":columns === 4, 
                                        "md:col-span-4":columns === 3, 
                                    })}
                                    key={getModelFromDescription(category.description)}
                                >
                                    <RadioButton
                                        input={{
                                            checked:
                                                variantCategory?.description?.includes(getModelFromDescription(category.description)),
                                            value: getModelFromDescription(category.description),
                                            onChange: (e) => {
                                                handleUpdateVariantCategory(category);
                                            },
                                            name: "category",
                                        }}
                                        field={{
                                            titel: getModelFromDescription(category.description),
                                            image: category.image,
                                        }}
                                        meta={{}}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {PRODUCT_CONFIGURATION_STEPS.VARINAT_SELECTION()
                        .enabled && variantCategory && (
                        <>
                            <h3 className={"mt-9"}>
                                <Trans i18nKey="orderEntry.variantSelect.heading">
                                    Please choose a size
                                </Trans>
                            </h3>
                            <div className={"grid grid-cols-12 gap-6"}>
                                {categories.filter(category => {
                                    const selectedCategoryModelName = getModelFromDescription(variantCategory.description);
                                    return category.description.includes(selectedCategoryModelName);
                                }).map((category) => (
                                    <div
                                        className={classNames({
                                            "col-span-6":true, 
                                            "md:col-span-3":columns === 4, 
                                            "md:col-span-4":columns === 3, 
                                        })}
                                        key={category.id}
                                    >
                                        <RadioButton
                                            input={{
                                                checked:
                                                    selectedCategory?.id ===
                                                    category.id,
                                                value: category.id,
                                                onChange: (e) => {
                                                    handleUpdateSize(category);
                                                },
                                                name: "category",
                                            }}
                                            field={{
                                                titel: getVariantFromDescription(category.description),
                                                image: category.image,
                                            }}
                                            meta={{}}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {(PRODUCT_CONFIGURATION_STEPS.PRODUCT_SELECTION()
                        .enabled || PRODUCT_CONFIGURATION_STEPS.VARINAT_SELECTION().enabled) && !hideProducts && (
                        <>
                            <h3 className={"mt-9"}>
                                <Trans i18nKey="orderEntry.productSelect.heading">
                                    Please select your product
                                </Trans>
                            </h3>
                            <div className={"grid grid-cols-12 gap-6 mt-6"}>
                                {selectedCategory &&
                                    models.map((item, index) => {
                                        const product =
                                            selectedCategory.products.find(
                                                (product) => {
                                                    const currentModel =
                                                        !disableColorSelection ? getModelFromDescription(
                                                            product.description
                                                        ) : product.description;
                                                    return (
                                                        currentModel === item
                                                    );
                                                }
                                            );

                                        if (!product) return null;

                                        const fieldIndex = getProductFieldIndex(product.id);

                                        return (
                                            <div
                                                className={
                                                    classNames({
                                                        "col-span-12":true, 
                                                        "md:col-span-3":columns === 4, 
                                                        "md:col-span-4":columns === 3, 
                                                    })}
                                                key={item}
                                            >
                                                {!isForSales ? (
                                                    <RadioButton
                                                        input={{
                                                            name: "product",
                                                            type: "radio",
                                                            value: item,
                                                            checked:
                                                                item === model,
                                                            onChange: (e) => {
                                                                form.change(
                                                                    "model",
                                                                    item
                                                                );
                                                                form.change(
                                                                    "color",
                                                                    null
                                                                );
                                                                const product =
                                                                    selectedCategory.products.find(
                                                                        (
                                                                            product
                                                                        ) => {
                                                                            const currentModel =
                                                                                !disableColorSelection ? getModelFromDescription(
                                                                                    product.description
                                                                                ) : product.description;
                                                                            return (
                                                                                currentModel ===
                                                                                item
                                                                            );
                                                                        }
                                                                    );

                                                                setSelectedProduct(
                                                                    {
                                                                        isLastStep:
                                                                            PRODUCT_CONFIGURATION_STEPS.PRODUCT_SELECTION()
                                                                                .isLastStep,
                                                                        product,
                                                                    }
                                                                );
                                                                orderEntryDispatch(
                                                                    {
                                                                        type: "setSerialNumber",
                                                                        value: {},
                                                                    }
                                                                );
                                                            },
                                                        }}
                                                        field={{
                                                            titel: item,
                                                            image: product?.image,
                                                            // description: item.description,
                                                        }}
                                                        meta={{}}
                                                    />
                                                ) : (
                                                    <Field
                                                        name={`products[${fieldIndex}]`}
                                                        type={"text"}
                                                        component={
                                                            RadioBigWithAmount
                                                        }
                                                        validate={value => (hasProducts(productValues) ? undefined : <Trans i18nKey="productSelect.required">Required</Trans>)}
                                                        field={{
                                                            titel: product?.description,
                                                            description: product?.text,
                                                            image: product?.image,
                                                            price: product?.price,
                                                            maxQuantity: Math.min(product?.stockQuantity ?? 0, maxQuantity ?? 99),
                                                            hideProductDetailsButton,
                                                            hideUnavailableContactLink,
                                                        }}
                                                        // RadioBigWithAmount is only interested in the amount value
                                                        format={(value) =>
                                                            value?.amount
                                                        }
                                                        // In the form itself, we want to persist the item object with the amount in an array
                                                        onChange={(amount) => {
                                                            if (Number(amount) === 0) {
                                                                removeProduct(product.id)
                                                                return;
                                                            }

                                                            const isUpdate = productExists(product.id);

                                                            if (isUpdate) {
                                                                updateProduct(product.id, { amount, type: isForSales ? "shop" : null });
                                                            } else {
                                                                addProduct({
                                                                    ...product,
                                                                    amount,
                                                                    type: isForSales ? "shop" : null,
                                                                });
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </>
                    )}

                    {showSelfRepairGuides && (PRODUCT_CONFIGURATION_STEPS.PRODUCT_SELECTION().enabled || PRODUCT_CONFIGURATION_STEPS.VARINAT_SELECTION().enabled) && (
                        <SelfRepairGuidesList columns={columns} showOtherAvailableDocuments={showOtherAvailableDocuments} />
                    )}

                    {PRODUCT_CONFIGURATION_STEPS.COLOR_SELECTION().enabled && (
                        <>
                            <h3 className={"mt-9"}>
                                <Trans i18nKey="orderEntry.productSelect.color">
                                    Please select a color
                                </Trans>
                            </h3>
                            <div className={"grid grid-cols-10 gap-6 mt-6"}>
                                {[
                                    {
                                        id: "RD",
                                        label: i18n.t(`common.red`),
                                        color: "#ff0000",
                                    },
                                    {
                                        id: "RG",
                                        label: i18n.t(`common.royalGreen`),
                                        color: "#00b926",
                                    },
                                    {
                                        id: "BL",
                                        label: i18n.t(`common.blue`),
                                        color: "#0000ff",
                                    },
                                    {
                                        id: "BK",
                                        label: i18n.t(`common.black`),
                                        color: "#000000",
                                    },
                                    {
                                        id: "WT",
                                        label: i18n.t(`common.white`),
                                        color: "#ffffff",
                                    },
                                    {
                                        id: "BG",
                                        label: i18n.t(`common.blackGold`),
                                        color: "#ffbc00",
                                    },
                                    {
                                        id: "GY",
                                        label: i18n.t(`common.gray`),
                                        color: "#6c6c6c",
                                    },
                                    {
                                        id: "GD",
                                        label: i18n.t(`common.gold`),
                                        color: "#FFD700",
                                    },
                                ]
                                    .filter((item) => colors.includes(item.id))
                                    .map((item) => (
                                        <div
                                            className={
                                                "col-span-5 md:col-span-2"
                                            }
                                            key={item.id}
                                        >
                                            <RadioButton
                                                input={{
                                                    name: "product",
                                                    type: "radio",
                                                    value: item.id,
                                                    checked: item.id === color,
                                                    onChange: (e) => {
                                                        form.change(
                                                            "color",
                                                            item.id
                                                        );
                                                        const product =
                                                            selectedCategory.products.find(
                                                                (product) => {
                                                                    const currentModel =
                                                                        !disableColorSelection ? getModelFromDescription(
                                                                            product.description
                                                                        ) : product.description;
                                                                    const currentColor =
                                                                        getColorFromDescription(
                                                                            product.description
                                                                        );

                                                                    return (
                                                                        currentModel ===
                                                                            model &&
                                                                        currentColor ===
                                                                            item.id
                                                                    );
                                                                }
                                                            );
                                                        setSelectedProduct({
                                                            isLastStep:
                                                                PRODUCT_CONFIGURATION_STEPS.COLOR_SELECTION()
                                                                    .isLastStep,
                                                            product,
                                                        });
                                                        orderEntryDispatch({
                                                            type: "setSerialNumber",
                                                            value: {},
                                                        });
                                                    },
                                                }}
                                                field={{
                                                    titel: item.label,
                                                    color: item.color,
                                                    // description: item.description,
                                                }}
                                                meta={{}}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}
                </div>

                <ProductModal
                    title="iPhone 6s Akku"
                    description="Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet."
                    imgUrl="https://store.ifixit.de/cdn/shop/products/nVVNTsEwASm5TAy1_3aab3373-9368-4143-aed1-1fe7112d786d_2048x.jpg?v=1698275004"
                    isOpen={isProductModalOpen}
                    onClose={() => setIsProductModalOpen(false)}
                />
            </div>
        );
    },
    function areEqual(prevProps, nextProps) {
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);
